import { useTranslation } from 'react-i18next'

import type { TenantSupportContact } from '@/shared/lib/tenant-suspended'

interface Props {
  tenantName?: string
  support?: TenantSupportContact
}

export function TenantSuspendedPage({ tenantName, support }: Props) {
  const { t } = useTranslation()
  const hasContact = Boolean(
    support?.phone?.trim() || support?.email?.trim() || support?.qrcode_url?.trim(),
  )

  return (
    <div className="flex min-h-[50vh] flex-col items-center justify-center px-6 py-16 text-center">
      <div className="w-full max-w-lg rounded-2xl border border-zinc-800/80 bg-zinc-900/60 px-8 py-10 shadow-xl">
        <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-amber-500/15 text-2xl text-amber-400">
          ⏸
        </div>
        <h1 className="text-xl font-semibold text-zinc-100">
          {t('tenant.suspended.title')}
        </h1>
        {tenantName ? (
          <p className="mt-2 text-sm text-zinc-400">
            {t('tenant.suspended.tenantLabel', { name: tenantName })}
          </p>
        ) : null}
        <p className="mt-4 text-sm leading-relaxed text-zinc-400">
          {support?.message?.trim() || t('tenant.suspended.description')}
        </p>

        {hasContact ? (
          <div className="mt-8 space-y-4 text-left text-sm">
            <p className="text-center text-xs font-medium uppercase tracking-wide text-zinc-500">
              {t('tenant.suspended.contactTitle')}
            </p>
            {support?.phone?.trim() ? (
              <div className="rounded-lg bg-zinc-950/50 px-4 py-3">
                <div className="text-xs text-zinc-500">{t('tenant.suspended.phone')}</div>
                <a
                  href={`tel:${support.phone.trim()}`}
                  className="mt-1 block font-medium text-violet-300 hover:text-violet-200"
                >
                  {support.phone.trim()}
                </a>
              </div>
            ) : null}
            {support?.email?.trim() ? (
              <div className="rounded-lg bg-zinc-950/50 px-4 py-3">
                <div className="text-xs text-zinc-500">{t('tenant.suspended.email')}</div>
                <a
                  href={`mailto:${support.email.trim()}`}
                  className="mt-1 block break-all font-medium text-violet-300 hover:text-violet-200"
                >
                  {support.email.trim()}
                </a>
              </div>
            ) : null}
            {support?.qrcode_url?.trim() ? (
              <div className="flex flex-col items-center rounded-lg bg-zinc-950/50 px-4 py-4">
                <div className="mb-3 text-xs text-zinc-500">{t('tenant.suspended.qrcode')}</div>
                <img
                  src={support.qrcode_url.trim()}
                  alt={t('tenant.suspended.qrcode')}
                  className="h-36 w-36 rounded-lg border border-zinc-800 bg-white object-contain p-1"
                />
              </div>
            ) : null}
          </div>
        ) : (
          <p className="mt-6 text-xs text-zinc-500">{t('tenant.suspended.noContact')}</p>
        )}
      </div>
    </div>
  )
}
